import { useEffect, useState, useRef } from "react";
import ChatBoxHeader from "./ChatBoxHeader";
import ChatBoxSendForm from "./ChatBoxSendForm";
import { ChatThread, ChatMessage, listChatMessages, sendChatMessage, pollForNewMessages } from "../../lib/api/chat";

interface ChatBoxProps {
  thread: ChatThread;
  onPlanModified?: () => void;
}

export default function ChatBox({ thread, onPlanModified }: ChatBoxProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const loadMessages = async () => {
      try {
        setLoading(true);
        setError(null);
        const loadedMessages = await listChatMessages(thread.id, 100, 0, true);
        setMessages(Array.isArray(loadedMessages) ? loadedMessages : []);
      } catch (err) {
        console.error("[ChatBox] Error loading messages:", err);
        setError(err instanceof Error ? err.message : "Failed to load messages");
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [thread.id]);

  const handleSendMessage = async (messageContent: string) => {
    if (!messageContent.trim() || isSending) return;

    setIsSending(true);
    setError(null);

    try {
      console.log("[ChatBox] Sending message:", messageContent);
      const response = await sendChatMessage(thread.id, {
        content: messageContent,
        allow_plan_changes: true,
      });

      console.log("[ChatBox] User message sent, ID:", response.user_message.id);
      setMessages((prev) => [...prev, response.user_message]);

      const pollingInterval = response.polling_interval_ms || 1000;
      console.log(`[ChatBox] Starting to poll for response (interval: ${pollingInterval}ms)`);

      const assistantMessage = await pollForNewMessages(thread.id, response.user_message.id, pollingInterval, 30);

      if (assistantMessage) {
        console.log("[ChatBox] Assistant message received, adding to UI");
        console.log("[ChatBox] Tool calls:", assistantMessage.tool_calls);
        console.log("[ChatBox] Metadata:", assistantMessage.metadata);
        
        setMessages((prev) => [...prev, assistantMessage]);
        
        // Check if modifications actually succeeded (metadata will have plan_modified flag after backend processes)
        const toolCalls = assistantMessage.tool_calls || [];
        
        // If tool calls were made, refresh plan to check if they succeeded
        if (toolCalls.length > 0) {
          console.log("[ChatBox] Plan modifications attempted, will verify success...");
          if (onPlanModified) {
            // Tool calls are executed asynchronously on the backend
            // Retry refresh multiple times with increasing delays
            const refreshAttempts = [2000, 4000, 6000, 8000, 10000];
            
            refreshAttempts.forEach((delay, index) => {
              setTimeout(() => {
                console.log(`[ChatBox] Refresh attempt ${index + 1} after ${delay}ms`);
                onPlanModified();
              }, delay);
            });
          }
        }
      } else {
        console.error("[ChatBox] No assistant response received within timeout");
        setError("AI response timed out. Please try again.");
      }
    } catch (err) {
      console.error("[ChatBox] Failed to send message:", err);
      setError(err instanceof Error ? err.message : "Failed to send message");

      try {
        console.log("[ChatBox] Reloading all messages after error");
        const updatedMessages = await listChatMessages(thread.id);
        setMessages(Array.isArray(updatedMessages) ? updatedMessages : []);
      } catch (reloadErr) {
        console.error("[ChatBox] Failed to reload messages:", reloadErr);
      }
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-r-transparent mb-3"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading chat history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
      {/* Chat Box Header */}
      <ChatBoxHeader />

      {/* Messages Container */}
      <div className="flex-1 max-h-full p-5 space-y-4 overflow-auto custom-scrollbar xl:space-y-6 xl:p-6">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <p className="text-sm">Start a conversation about your plugging plan</p>
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-brand-700 dark:text-brand-400">AI</span>
                </div>
              )}

              <div className={`max-w-[80%] ${message.role === "user" ? "text-right" : ""}`}>
                <div
                  className={`px-4 py-3 rounded-lg ${
                    message.role === "user"
                      ? "bg-brand-500 text-white dark:bg-brand-500 rounded-tr-sm"
                      : "bg-gray-100 dark:bg-white/5 text-gray-800 dark:text-white/90 rounded-tl-sm"
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {message.tool_calls && message.tool_calls.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-300 dark:border-gray-600/50">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Used {message.tool_calls.length} tool{message.tool_calls.length > 1 ? "s" : ""}
                      </p>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-gray-500 text-xs dark:text-gray-400">
                  {formatTime(message.created_at)}
                </p>
              </div>

              {message.role === "user" && (
                <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-brand-700 dark:text-brand-400">You</span>
                </div>
              )}
            </div>
          ))
        )}

        {isSending && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-brand-700 dark:text-brand-400">AI</span>
            </div>
            <div className="bg-gray-100 dark:bg-white/5 rounded-lg rounded-tl-sm px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-r-transparent"></div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Thinking...</p>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="px-5 py-3 xl:px-6 bg-red-50 dark:bg-red-500/10 border-t border-red-200 dark:border-red-500/30">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Send Form */}
      <ChatBoxSendForm threadId={thread.id} onMessageSent={handleSendMessage} isSending={isSending} />
    </div>
  );
}
